import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import Checkbox from '../../../components/checkbox/checkbox.component';
import Badge from '../../../components/badge/badge.component';
import Menu from '../../../components/menu/menu.component';
import MenuItem from '../../../components/menu-item/menu-item.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import CheckboxLabel from '../../../components/checkbox-label/checkbox-label.component';
import TextBlock from '../../../components/text-block/text-block.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ControlButtonBlock from '../common/control-button-block/control-button-block.component';
import ControlContainer from '../../../components/control-container/control-container.component';
import ControlActionButton from '../../../components/control-action-button/control-action-button.component';
import ControlModeBlock from '../../../components/control-mode-block/control-mode-block.component';
import ControlModeButton from '../../../components/control-mode-button/control-mode-button.component';
import CheckboxSelectAll from '../../../components/checkbox-select-all/checkbox-select-all.component';
import FiltersModal from './filters-modal/filters-modal.component';
import ExportModal from './export-modal/export-modal.component';
import ImportModal from './import-modal/import-modal.component';
import TableColumnsModal from './table-columns-modal/table-columns-modal.component';
import EditIcon from '../../../icons/edit-icon';
import ImportIcon from '../../../icons/import-icon';
import ExportIcon from '../../../icons/export-icon';
import ExpandIcon from '../../../icons/expand-icon';
import CollapseIcon from '../../../icons/collapse-icon';
import CardViewIcon from '../../../icons/card-view-icon';
import TableViewIcon from '../../../icons/table-view-icon';
import SelectIcon from '../../../icons/select-icon';
import ConfigureIcon from '../../../icons/configure-icon';
import FiltersIcon from '../../../icons/filters-icon';
import ActionsIcon from '../../../icons/actions-icon';
import TableColumnsConfigureIcon from '../../../icons/table-columns-configure-icon';
import WidgetsConfigureIcon from '../../../icons/widgets-configure-icon';
import TabsConfigureIcon from '../../../icons/tabs-configure-icon';
import ChangeStatusIcon from '../../../icons/change-status-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import FormatService from '../../../services/format-service';
import useScreenMobile from '../../../hooks/use-screen-mobile';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  toggleEditMode, toggleExportMode, toggleCardView, toggleCustomizeMode, toggleSelectMode,
} from './controls.action';
import { QUERY_PARAM_EDIT_MODE } from '../../../constants/system/query-params';
import { TAB_NAME_SUMMARY } from '../../../constants/system/tab-names';

const Controls: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const INLINE_BUTTONS_LIMIT: number = 2;
  const ID_CHECKBOX_SELECT_ALL: string = 'checkbox-select-all';
  const ID_PREFIX_CHECKBOX_TAB_CONFIGURE_ITEM: string = 'checkbox-tab-configure-item';
  const ID_PREFIX_CHECKBOX_WIDGET_CONFIGURE_ITEM: string = 'checkbox-widget-configure-item';
  const BUTTON_LABEL_CLOSE: string = 'Close';

  const {
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isCardView,
    isTableLoading,
    isHidden,
    isFilterApplyDisabled,
    isRightDrawerOpen,
    isTableExport,
    isCustomizable,
    isGroupExpanded,
    isSaveDisabled,
    isExportDisabled,
    withDelete,
    withFilters,
    withStaticEdit,
    withNoMultiLine,
    withAllFiltersEmpty,
    withExportModal,
    withSelectAll,
    withAllRowsSelected,
    withCardView,
    withGroups,
    withNoEditMode,
    withSelectMode,
    withTabSettings,
    withWidgetSettings,
    withColumnSettings,
    createButtonTitle,
    createGroupButtonTitle,
    createAdditionalItemButtonTitle,
    createPermission,
    createGroupPermission,
    createAdditionalItemPermission,
    editPermissions,
    customActionMenuItems,
    widgetSettings,
    importExportTab,
    importTypeList,
    importFormatList,
    importPermission,
    labelSave,
    importExportEntityType,
    exportItemIds,
    portfolioIds,
    portfolioGroupIds,
    status,
    exportTypeList,
    exportFormatList,
    exportPermission,
    customizePermission,
    changeStatusPermission,
    deletePermission,
    dealId,
    tabSettings,
    customListOptions,
    filters,
    filterComponent,
    currentFilters,
    tableFields,
    tableFieldGroups,
    tableColumnSettings,
    rowsTotal,
    rowsSelectedTotal,
    selectedItemIds,
    onExpandAll,
    onCollapseAll,
    onApplyFilters,
    onResetFilters,
    onImportSuccess,
    onExportSuccess,
    onCancelForm,
    onSubmitForm,
    onExportClick,
    onStatusChangeClick,
    onDeleteClick,
    onSelectAllRows,
    onCreateButtonClick,
    onCreateGroupButtonClick,
    onCreateAdditionalItemButtonClick,
    onTableColumnsChange,
    onTabSettingsChange,
    onWidgetSettingsChange,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isMobileScreen: boolean = useScreenMobile();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryEditMode: boolean | null = locationQueryParams && locationQueryParams[QUERY_PARAM_EDIT_MODE]
    ? FormatService.extractBooleanValue(locationQueryParams[QUERY_PARAM_EDIT_MODE].toString()) : null;
  const withImport: boolean = importFormatList && !!importFormatList.length;
  const withExport: boolean = exportFormatList && !!exportFormatList.length;
  const isBlank: boolean = rowsTotal === 0;
  const labelCancel: string | null = isCustomizeMode ? BUTTON_LABEL_CLOSE : null;

  const [isExportModalOpen, setIsExportModalOpen]: [boolean, Function] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen]: [boolean, Function] = useState(false);
  const [isFiltersModalOpen, setIsFilterModalOpen]: [boolean, Function] = useState(false);
  const [isTableColumnsModalOpen, setIsTableColumnsModalOpen]: [boolean, Function] = useState(false);
  const [exportEntityIds, setExportEntityIds]: [number[] | null, Function] = useState(null);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const importPermissions: string[] = useMemo((): string[] => (
    importPermission ? [importPermission] : []
  ), [importPermission]);
  const exportPermissions: string[] = useMemo((): string[] => (
    exportPermission ? [exportPermission] : []
  ), [exportPermission]);
  const customizePermissions: string[] = useMemo((): string[] => (
    customizePermission ? [customizePermission] : []
  ), [customizePermission]);
  const createPermissions: string[] = useMemo((): string[] => (
    createPermission ? [createPermission] : []
  ), [createPermission]);
  const createGroupPermissions: string[] = useMemo((): string[] => (
    createGroupPermission ? [createGroupPermission] : []
  ), [createGroupPermission]);
  const createAdditionalItemPermissions: string[] = useMemo((): string[] => (
    createAdditionalItemPermission ? [createAdditionalItemPermission] : []
  ), [createAdditionalItemPermission]);
  const deletePermissions: string[] = useMemo((): string[] => (
    deletePermission ? [deletePermission] : []
  ), [deletePermission]);

  const withButtonsGroup: boolean = !!createButtonTitle
    && (!!createGroupButtonTitle || !!createAdditionalItemButtonTitle);
  const isMultiline: boolean = withButtonsGroup && !withNoMultiLine;
  const selectedFiltersTotal: number = filters && (Object.values(filters) as FilterValue[])
    .filter((filterValue: FilterValue): boolean => !!filterValue || filterValue === false).length;
  const isModeActive: boolean = (isExportMode && isTableExport) || isEditMode || isSelectMode || isCustomizeMode;

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const openExportModal: MouseEventHandlerFunction = useCallback((): void => {
    setExportEntityIds(exportItemIds);
    setIsExportModalOpen(true);
  }, [exportItemIds]);

  const closeExportModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsExportModalOpen(false);
    setExportEntityIds(null);
  }, []);

  const openImportModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsImportModalOpen(true);
  }, []);

  const closeImportModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsImportModalOpen(false);
  }, []);

  const openFiltersModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsFilterModalOpen(true);
  }, []);

  const closeFiltersModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsFilterModalOpen(false);
  }, []);

  const openTableColumnsModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsTableColumnsModalOpen(true);
  }, []);

  const closeTableColumnsModal: MouseEventHandlerFunction = useCallback((): void => {
    setIsTableColumnsModalOpen(false);
  }, []);

  const onEditModeButtonClick: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleEditMode(true));
  }, []);

  const onSelectModeButtonClick: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleSelectMode(!isSelectMode));
  }, [isSelectMode]);

  const onEnableCardView: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleCardView(true));
  }, []);

  const onDisableCardView: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleCardView(false));
  }, []);

  const onEnableExportMode: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleExportMode(true));
  }, []);

  const onEnableCustomizeMode: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleCustomizeMode(true));
  }, []);

  const onCheckIsMenuItemVisible: Function = useCallback((actionMenuItem: ActionMenuItem): boolean => (
    !!actionMenuItem.onClick && !actionMenuItem.isHidden
  ), []);

  const changeVisibilityTabSettings: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tabSettingId: number,
  ): void => {
    const newTabSettings: TabItem[] = tabSettings.map((tabItem: TabItem): TabItem => ({
      id: tabItem.id,
      name: tabItem.name,
      title: tabItem.title,
      isHidden: tabSettingId === tabItem.id ? !tabItem.isHidden : tabItem.isHidden,
    }));

    if (onTabSettingsChange) {
      onTabSettingsChange(newTabSettings);
    }

    event.stopPropagation();
  }, [tabSettings]);

  const changeVisibilityWidgetSettings: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    widgetName: string,
    isVisible: boolean,
  ): void => {
    if (onWidgetSettingsChange) {
      onWidgetSettingsChange(widgetName, {
        isVisible,
      });
    }

    event.stopPropagation();
  }, [widgetSettings]);

  const onMenuItemClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.preventDefault();
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <EditIcon />,
      text: 'Edit',
      onClick: onEditModeButtonClick,
      permissions: editPermissions,
      isHidden: !!withNoEditMode || (isBlank && !withStaticEdit),
    },
    {
      id: 2,
      icon: <ImportIcon />,
      text: 'Import',
      onClick: openImportModal,
      permission: importPermission,
      isHidden: !withImport,
    },
    {
      id: 3,
      icon: <ExportIcon />,
      text: 'Export',
      onClick: isTableExport ? onEnableExportMode : openExportModal,
      permission: exportPermission,
      isHidden: !withExport || isBlank,
    },
    {
      id: 4,
      icon: <ConfigureIcon />,
      text: 'Customize',
      onClick: onEnableCustomizeMode,
      permission: customizePermission || null,
      isHidden: (withCardView && isCardView) || !isCustomizable,
    },
    {
      id: 5,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      onClick: onDeleteClick,
      permission: deletePermission || null,
      isHidden: !withDelete,
    },
  ], [
    importPermission, exportPermission, customizePermission, editPermissions, deletePermission, withCardView,
    isCardView, isCustomizable, withNoEditMode, withExport, withImport, isBlank, withStaticEdit, withDelete,
  ]);

  const selectModeMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ChangeStatusIcon />,
      text: 'Change Status',
      onClick: onStatusChangeClick,
      permission: changeStatusPermission,
    },
    {
      id: 2,
      icon: <ExportIcon />,
      text: 'Export',
      onClick: onExportClick,
      permission: exportPermission,
    },
    {
      id: 3,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      onClick: onDeleteClick,
      permission: deletePermission,
    },
  ], [changeStatusPermission, exportPermission, deletePermission, selectedItemIds]);

  const commonSelectModeMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => (
    customListOptions && customListOptions.length ? [
      ...(customListOptions || []),
      ...selectModeMenuItems.map((menuItem: ActionMenuItem): ActionMenuItem => ({
        ...menuItem,
        id: menuItem.id + customListOptions.length,
      })),
    ] : selectModeMenuItems
  ), [customListOptions, selectModeMenuItems]);

  const selectModeMenuOptionPermissions: string[] = useMemo((): string[] => (
    (commonSelectModeMenuItems
      ? commonSelectModeMenuItems.map((menuItem: ActionMenuItem): string | undefined => menuItem.permission)
      : []).filter((permissionItem: string | undefined): boolean => !!permissionItem) as string[]
  ), [commonSelectModeMenuItems]);

  const commonActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => ([
    ...(actionMenuItems || []),
    ...((customActionMenuItems || []).filter((actionMenuItem: ActionMenuItem): boolean => !!actionMenuItem.id)
      .map((actionMenuItem: ActionMenuItem): ActionMenuItem => ({
        ...actionMenuItem,
        id: actionMenuItems.length + actionMenuItem.id,
      })) || []),
  ]), [actionMenuItems, customActionMenuItems]);

  const actionMenuPermissions: string[] = useMemo((): string[] => (
    commonActionMenuItems.map((actionMenuItem: ActionMenuItem): string | string[] => (
      actionMenuItem.permission || actionMenuItem.permissions || []
    )).flat()
  ), [commonActionMenuItems]);

  const actionVisibleItemsTotal: number = useMemo((): number => (
    commonActionMenuItems.filter((actionMenuItem: ActionMenuItem): boolean => (
      onCheckIsMenuItemVisible(actionMenuItem)
    )).length
  ), [commonActionMenuItems, menuAnchor]);

  const withOpenItems: boolean = commonActionMenuItems.reduce((
    result: boolean, actionMenuItem: ActionMenuItem,
  ): boolean => (
    result || (!actionMenuItem.permission && !actionMenuItem.permissions)
  ), false);

  const withVisibleItems: boolean = !!actionVisibleItemsTotal;
  const isInlineButtonsView: boolean = actionVisibleItemsTotal <= INLINE_BUTTONS_LIMIT && !customActionMenuItems;

  useEffect((): void => {
    if (exportItemIds && !exportEntityIds) {
      setExportEntityIds(exportItemIds);
    }
  }, [exportItemIds]);

  useEffect((): void => {
    dispatch(toggleEditMode(!!queryEditMode));
  }, []);

  return (
    <ControlContainer isHidden={isHidden} isMultiline={isMultiline}>
      <FlexContainer isStart={isMultiline} isFullWidth={isMultiline}>
        {
          (withSelectAll && (isExportMode || isSelectMode)) && !isBlank && (
            <div>
              <CheckboxSelectAll
                isCard={isCardView}
                isChecked={withAllRowsSelected || rowsSelectedTotal === rowsTotal}
                isIndeterminate={!withAllRowsSelected && rowsSelectedTotal > 0 && rowsSelectedTotal < rowsTotal}
                id={ID_CHECKBOX_SELECT_ALL}
                onChange={onSelectAllRows}
              />
              <TextBlock>
                Select All
              </TextBlock>
            </div>
          )
        }
        {
          !!createButtonTitle && !!onCreateButtonClick && (
            <div>
              <CheckAccess permissions={createPermissions}>
                <ControlActionButton title={createButtonTitle} onClick={onCreateButtonClick} />
              </CheckAccess>
            </div>
          )
        }
        {
          !!createGroupButtonTitle && !!onCreateGroupButtonClick && (
            <CheckAccess permissions={createGroupPermissions}>
              <ControlActionButton title={createGroupButtonTitle} onClick={onCreateGroupButtonClick} />
            </CheckAccess>
          )
        }
        {
          !!createAdditionalItemButtonTitle && !!onCreateAdditionalItemButtonClick && !isMobileScreen && (
            <CheckAccess permissions={createAdditionalItemPermissions}>
              <ControlActionButton
                title={createAdditionalItemButtonTitle}
                onClick={onCreateAdditionalItemButtonClick}
              />
            </CheckAccess>
          )
        }
      </FlexContainer>
      <ControlModeBlock>
        {
          withCardView && !isCustomizeMode && (
            <div>
              {
                !isCardView && (
                  <ControlModeButton title="Card View" onClick={onEnableCardView} iconComponent={<CardViewIcon />} />
                )
              }
              {
                isCardView && (
                  <ControlModeButton
                    title="Table View"
                    onClick={onDisableCardView}
                    iconComponent={<TableViewIcon />}
                  />
                )
              }
            </div>
          )
        }
        {
          withGroups && !isBlank && (
            <div>
              {
                !isGroupExpanded && (
                  <ControlModeButton title="Expand All" onClick={onExpandAll} iconComponent={<ExpandIcon />} />
                )
              }
              {
                isGroupExpanded && (
                  <ControlModeButton title="Collapse All" onClick={onCollapseAll} iconComponent={<CollapseIcon />} />
                )
              }
            </div>
          )
        }
        {
          withSelectMode && !isBlank && !isSelectMode && !isExportMode && !isEditMode && !isCustomizeMode && (
            <div>
              <ControlModeButton
                isActive={isModeActive}
                title="Select"
                onClick={onSelectModeButtonClick}
                iconComponent={<SelectIcon />}
              />
            </div>
          )
        }
        {
          isInlineButtonsView && !isEditMode && !isCustomizeMode && !isSelectMode && !isExportMode
          && (withImport || (withExport && !isBlank)) && (
            <div>
              {
                withImport && (
                  <CheckAccess permissions={importPermissions}>
                    <ControlModeButton title="Import" onClick={openImportModal} iconComponent={<ImportIcon />} />
                  </CheckAccess>
                )
              }
              {
                withExport && !isBlank && (
                  <CheckAccess permissions={exportPermissions}>
                    <ControlModeButton
                      isActive={isModeActive}
                      title="Export"
                      onClick={isTableExport ? onEnableExportMode : openExportModal}
                      iconComponent={<ExportIcon />}
                    />
                  </CheckAccess>
                )
              }
            </div>
          )
        }
        {
          isInlineButtonsView && !isSelectMode && !isExportMode && !isEditMode
          && !isCustomizeMode && !withNoEditMode && (!isBlank || withStaticEdit) && (
            <div>
              <CheckAccess permissions={editPermissions}>
                <ControlModeButton
                  isActive={isModeActive}
                  title="Edit"
                  onClick={onEditModeButtonClick}
                  iconComponent={<EditIcon />}
                />
              </CheckAccess>
            </div>
          )
        }
        {
          isInlineButtonsView && !isSelectMode && !isExportMode && !isEditMode
          && !isCustomizeMode && withDelete && (
            <div>
              <CheckAccess permissions={deletePermissions}>
                <ControlModeButton title="Delete" onClick={onDeleteClick} iconComponent={<DeleteItemIcon />} />
              </CheckAccess>
            </div>
          )
        }
        {
          withWidgetSettings && !isExportMode && !isSelectMode && !isEditMode && (
            <div>
              <ControlModeButton title="Widgets" onClick={openActionMenu} iconComponent={<WidgetsConfigureIcon />} />
              <Menu isFlat isBordered menuAnchor={menuAnchor} onClose={closeActionMenu}>
                {
                  widgetSettings && !!widgetSettings.length && widgetSettings.map((
                    widgetSettingItem: WidgetItem, widgetSettingIndex: number,
                  ): ReactElement => (
                    <CheckAccess
                      key={widgetSettingIndex + 1}
                      permissions={widgetSettingItem.permission ? [widgetSettingItem.permission] : []}
                    >
                      <MenuItem
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          changeVisibilityWidgetSettings(event, widgetSettingItem.name, !widgetSettingItem.isVisible);
                        }}
                      >
                        <CheckboxLabel label={widgetSettingItem.title} onClick={onMenuItemClick}>
                          <Checkbox
                            isChecked={widgetSettingItem.isVisible}
                            id={`${ID_PREFIX_CHECKBOX_WIDGET_CONFIGURE_ITEM}-${widgetSettingIndex + 1}`}
                          />
                        </CheckboxLabel>
                      </MenuItem>
                    </CheckAccess>
                  ))
                }
              </Menu>
            </div>
          )
        }
        {
          withTabSettings && !isExportMode && !isSelectMode && !isEditMode && (
            <div>
              <ControlModeButton title="Tabs" onClick={openActionMenu} iconComponent={<TabsConfigureIcon />} />
              <Menu isFlat isBordered menuAnchor={menuAnchor} onClose={closeActionMenu}>
                {
                  tabSettings && !!tabSettings.length && tabSettings.map((
                    tabItem: TabItem, tabItemIndex: number,
                  ): ReactElement | boolean => (
                    tabItem.name !== TAB_NAME_SUMMARY && (
                      <CheckAccess
                        key={tabItem.id}
                        permissions={tabItem.permission ? [tabItem.permission] : []}
                      >
                        <MenuItem
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            changeVisibilityTabSettings(event, tabItem.id);
                          }}
                        >
                          <CheckboxLabel label={tabItem.title} onClick={onMenuItemClick}>
                            <Checkbox
                              isChecked={!tabItem.isHidden}
                              id={`${ID_PREFIX_CHECKBOX_TAB_CONFIGURE_ITEM}-${tabItemIndex + 1}`}
                            />
                          </CheckboxLabel>
                        </MenuItem>
                      </CheckAccess>
                    )
                  ))
                }
              </Menu>
            </div>
          )
        }
        {
          withColumnSettings && !isExportMode && !isSelectMode && !isEditMode && !isSmallScreen && (
            <div>
              <ControlModeButton
                isHidden={isSmallScreen}
                title="Columns"
                onClick={openTableColumnsModal}
                iconComponent={<TableColumnsConfigureIcon />}
              />
            </div>
          )
        }
        {
          isInlineButtonsView && isCustomizable && !isSelectMode
          && !isCustomizeMode && !isEditMode && !isExportMode && ((withCardView && !isCardView) || !withCardView) && (
            <div>
              <CheckAccess permissions={customizePermissions}>
                <ControlModeButton
                  isActive={isModeActive}
                  title="Customize"
                  onClick={onEnableCustomizeMode}
                  iconComponent={<ConfigureIcon />}
                />
              </CheckAccess>
            </div>
          )
        }
        {
          isSelectMode && !!rowsSelectedTotal && (
            <CheckAccess permissions={selectModeMenuOptionPermissions}>
              <div>
                <ControlModeButton
                  title="Actions"
                  onClick={openActionMenu}
                  iconComponent={<ActionsIcon />}
                />
                <ContextMenu
                  menuAnchor={menuAnchor}
                  actionMenuItems={commonSelectModeMenuItems}
                  onClose={closeActionMenu}
                  onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
                />
              </div>
            </CheckAccess>
          )
        }
        {
          withFilters && !isSelectMode && !isExportMode && !isEditMode && !isCustomizeMode && (
            <div>
              <ControlModeButton
                isActive={!withAllFiltersEmpty}
                title="Filters"
                onClick={openFiltersModal}
                iconComponent={(
                  selectedFiltersTotal ? (
                    <Badge isCompact content={selectedFiltersTotal}>
                      <FiltersIcon />
                    </Badge>
                  ) : (
                    <FiltersIcon />
                  )
                )}
              />
            </div>
          )
        }
        {
          !isInlineButtonsView && !isSelectMode && !isExportMode && !isEditMode
          && !isCustomizeMode && withVisibleItems && (
            <CheckAccess permissions={!withOpenItems ? actionMenuPermissions : []}>
              <div>
                <ControlModeButton
                  title="Actions"
                  onClick={openActionMenu}
                  iconComponent={<ActionsIcon />}
                />
                <ContextMenu
                  menuAnchor={menuAnchor}
                  actionMenuItems={commonActionMenuItems}
                  onClose={closeActionMenu}
                  onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
                />
              </div>
            </CheckAccess>
          )
        }
        <ControlButtonBlock
          isIconView
          isExportMode={isExportMode}
          isEditMode={isEditMode}
          isSelectMode={isSelectMode}
          isCustomizeMode={isCustomizeMode}
          isSaveDisabled={isSaveDisabled}
          isExportDisabled={isExportDisabled}
          withOffset={isRightDrawerOpen}
          labelSave={labelSave}
          labelCancel={labelCancel}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onExportClick={onExportClick}
        />
        <ImportModal
          isOpen={isImportModalOpen}
          dealId={dealId}
          importEntityType={importExportEntityType}
          importFormatList={importFormatList}
          importTypeList={importTypeList}
          importTab={importExportTab}
          onSuccess={onImportSuccess}
          onCloseModal={closeImportModal}
        />
        {
          withColumnSettings && (
            <TableColumnsModal
              isOpen={isTableColumnsModalOpen}
              isDisabled={isTableLoading}
              tableFields={tableFields}
              tableFieldGroups={tableFieldGroups}
              tableColumnSettings={tableColumnSettings}
              onCloseModal={closeTableColumnsModal}
              onApplySettings={onTableColumnsChange}
            />
          )
        }
        {
          withExportModal && (
            <ExportModal
              isOpen={isExportModalOpen}
              withExportAll={withAllRowsSelected}
              dealId={dealId}
              entityIds={exportEntityIds}
              exportEntityType={importExportEntityType}
              exportFormatList={exportFormatList}
              exportTypeList={exportTypeList}
              exportTab={importExportTab}
              status={status}
              portfolioIds={portfolioIds}
              portfolioGroupIds={portfolioGroupIds}
              onSuccess={onExportSuccess}
              onCloseModal={closeExportModal}
            />
          )
        }
        {
          withFilters && (
            <FiltersModal
              isOpen={isFiltersModalOpen}
              isFilterApplyDisabled={isFilterApplyDisabled}
              filterComponent={filterComponent}
              currentFilters={currentFilters}
              onCloseModal={closeFiltersModal}
              onApplyFilters={onApplyFilters}
              onResetFilters={onResetFilters}
            />
          )
        }
      </ControlModeBlock>
    </ControlContainer>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    controls,
    rightDrawer,
  } = reducers;

  return {
    isExportMode: controls.isExportMode,
    isEditMode: controls.isEditMode,
    isCardView: controls.isCardView,
    isCustomizeMode: controls.isCustomizeMode,
    isSelectMode: controls.isSelectMode,
    isTableLoading: controls.isTableLoading,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
  };
};

export default connect(mapState)(Controls) as ReactComponent;
